// Import styles
import './style.scss';

// Import dependencies and utilities
const { isEmpty } = _uo.utility;

import { restCall } from '@builder/common/utilities';
import { recipeTypes as validRecipeTypes } from '@builder/config/valid-data';

import { html, render } from 'lit';

import { repeat } from 'lit/directives/repeat.js';
import { createRef, ref } from 'lit/directives/ref.js';

/**
 * Creates the recipe type selector
 * Uses Lit
 */
export default class RecipeTypeSelector {

	/**
	 * Create a reference to the dialog
	 *
	 * @type {Ref}
	 */
	$dialog = createRef();

	/**
	 * Invokes the method to render the dialog
	 *
	 * @param {Callback} onReady A callback invoked after the user set a recipe type
	 */
	constructor( onReady ) {
		// Save the callback
		this.onReady = onReady;

		// Render the dialog
		this.render();
	}

	/**
	 * Renders the dialog
	 *
	 * @return {undefined}
	 */
	render() {

		// Render
		render(
			html`
				<uo-dialog
					heading="${ UncannyAutomator.i18n.recipeType.title }"
					force-manual-close
					open

					${ ref( this.$dialog ) }
				>
					<div class="uap-recipe-type">
						${
							// Lit's repeat. If you need more information, check
							// https://lit.dev/docs/templates/directives/#repeat
							repeat(
								Object.values( validRecipeTypes ),
								( option ) => option.id,
								( option ) => html`
									<div
										class="uap-recipe-type-item"
										data-id=${ option.id }
										@click="${ () => {
											this.recipeType = option.id;
										} }"
										?selected=${ option.id === this.recipeType }
									>
										<h3>${ option.name }</h3>
										<p>${ option.description }</p>
									</div>
								`
							)
						}
					</div>

					<uo-alert
						type="info"
						heading="${ UncannyAutomator.i18n.recipeType.cantChangeLaterNotice }"
						class="uap-spacing-top uap-spacing-top--big"
					></uo-alert>
					
					<uo-button
						slot="secondary-action"
						color="secondary"
						href="${ UncannyAutomator.recipe.publish.moveToTrash.replaceAll( '&amp;', '&' ) }"
					>
						${ UncannyAutomator.i18n.cancel }
					</uo-button>

		    		<uo-button
						slot="primary-action"

						@click=${ ( event ) => {
							// Set loading animation on the button
							event.target.loading = true;

							// Save the recipe type
							this.saveRecipeType();
						} }

						?disabled=${ isEmpty( this.recipeType ) }
					>
						${ UncannyAutomator.i18n.confirm }
					</uo-button>
				</uo-dialog>
			`,
			document.body
		);

	}

	/**
	 * Saves the recipe type
	 *
	 * @return {undefined}
	 */
	saveRecipeType() {

		// Do REST call
		restCall(
			// Endpoint
			'change_post_recipe_type',

			// Data
			{
				// eslint-disable-next-line camelcase
				post_ID: UncannyAutomator.recipe.id,
				// eslint-disable-next-line camelcase
				recipe_type: this.recipeType,
			},

			// On success
			( response ) => {
				// Check if the call was successful
				if ( ! response.success ) {
					// Check if debugging is enabled
					if ( UncannyAutomatorBackend.debugging.enabled ) {
						// eslint-disable-next-line no-console
						console.error(
							// eslint-disable-next-line max-len
							"Recipe type selector: Couldn't set the recipe type. Open the Network tab and perform the action again."
						);
					}

					return;
				}

				// Save in main object
				UncannyAutomator.recipe.type = this.recipeType;

				// Close the dialog
				this.$dialog.value.close();

				// Invoke callback defined on the constructor to continue rendering the UI
				this.onReady();
			}
		);

	}

	/**
	 * The recipe type. When this changes, we have to render the dialog again
	 *
	 * @param {String} recipeType The recipe type
	 * @return {undefined}
	 */
	set recipeType( recipeType = '' ) {
		// Check if it's a valid recipe type
		if ( ! Object.keys( validRecipeTypes ).includes( recipeType ) ) {
			return;
		}

		// Save the value
		this.#_recipeType = recipeType;

		// Render the UI again
		this.render();
	}

	/**
	 * Returns the recipe type
	 *
	 * @return {String} The recipe type
	 */
	get recipeType() {
		return Object.keys( validRecipeTypes ).includes( this.#_recipeType ) ? this.#_recipeType : '';
	}

	#_recipeType;
}
